import { backtestStrategy, rankStrategies, scoreStrategySetup, strategies, V1_SYMBOLS, type Signal, type WatchSymbol } from "@tradesignal/core";

export const watchlist: WatchSymbol[] = [
  { id: "amd", symbol: "AMD", assetType: "stock", timeframe: "daily", enabled: true, notificationsEnabled: true },
  { id: "nvda", symbol: "NVDA", assetType: "stock", timeframe: "daily", enabled: true, notificationsEnabled: true },
  { id: "tsla", symbol: "TSLA", assetType: "stock", timeframe: "daily", enabled: true, notificationsEnabled: true },
  { id: "aapl", symbol: "AAPL", assetType: "stock", timeframe: "daily", enabled: true, notificationsEnabled: false },
  { id: "msft", symbol: "MSFT", assetType: "stock", timeframe: "daily", enabled: true, notificationsEnabled: false },
  { id: "spy", symbol: "SPY", assetType: "ETF", timeframe: "daily", enabled: true, notificationsEnabled: true },
  { id: "qqq", symbol: "QQQ", assetType: "ETF", timeframe: "daily", enabled: true, notificationsEnabled: true }
];

export function makeCandles(seed = 100, count = 260) {
  const candles = [];
  let price = seed;
  for (let i = 0; i < count; i += 1) {
    const trend = Math.sin(i / 13) * 0.7 + i * 0.025;
    const pull = Math.cos(i / 5) * 1.6;
    const close = Math.max(1, price + trend + pull);
    const open = price;
    const high = Math.max(open, close) + 1 + Math.sin(i) * 0.3;
    const low = Math.min(open, close) - 1 - Math.cos(i) * 0.3;
    candles.push({
      t: new Date(Date.now() - (count - i) * 86_400_000).toISOString(),
      o: Number(open.toFixed(2)),
      h: Number(high.toFixed(2)),
      l: Number(low.toFixed(2)),
      c: Number(close.toFixed(2)),
      v: Math.round(2_000_000 + Math.abs(Math.sin(i / 3)) * 1_400_000)
    });
    price = close;
  }
  return candles;
}

export const symbolCandles = Object.fromEntries(
  watchlist.map((item, index) => [item.symbol, makeCandles(item.assetType === "forex" ? 1 + index / 10 : 90 + index * 18)])
);

export const signals: Signal[] = watchlist
  .filter((item) => item.enabled)
  .map((item) =>
    scoreStrategySetup({
      symbol: item.symbol,
      assetType: item.assetType,
      strategyId: "trend-pullback",
      candles: symbolCandles[item.symbol],
      dataMeta: {
        source: "Mock Twelve Data daily",
        asOf: symbolCandles[item.symbol].at(-1)!.t,
        isDelayed: true,
        isComplete: true,
        interval: "1day"
      },
      freshness: "delayed"
    })
  )
  .filter(Boolean)
  .map((signal, index) => ({ ...signal!, confidence: Math.min(94, 80 + index * 3) }));

export const backtests = watchlist.flatMap((item) =>
  strategies
    .filter((strategy) => strategy.enabled && strategy.assetTypes.includes(item.assetType))
    .map((strategy) => backtestStrategy(item.symbol, item.assetType, strategy.id, symbolCandles[item.symbol]).metrics)
);

export const rankings = backtests.reduce<Record<string, typeof backtests>>((groups, metric) => {
  groups[metric.symbol] = [...(groups[metric.symbol] ?? []), metric];
  return groups;
}, {});
export const rankedBySymbol = Object.fromEntries(
  Object.entries(rankings).map(([symbol, metrics]) => [symbol, rankStrategies(symbol, metrics ?? [])])
);

export const trades = [
  {
    id: "trade-spy",
    symbol: "SPY",
    direction: "LONG",
    status: "entered",
    entry: 629.3,
    stopLoss: 616.4,
    target1: 648.65,
    target2: 661.55,
    latestPrice: 641.28,
    recommendation: "HOLD",
    notes: "Manual paper-tracking only. No broker connection.",
    updatedAt: new Date(Date.now() - 26 * 60 * 1000).toISOString()
  },
  {
    id: "trade-xau",
    symbol: "XAU/USD",
    direction: "LONG",
    status: "watching",
    entry: 2384.5,
    stopLoss: 2358.2,
    target1: 2423.95,
    target2: 2450.25,
    latestPrice: 2378.9,
    recommendation: "NO_ENTRY_YET",
    notes: "Waiting for entry trigger.",
    updatedAt: new Date(Date.now() - 52 * 60 * 1000).toISOString()
  }
].filter((trade) => V1_SYMBOLS.includes(trade.symbol as (typeof V1_SYMBOLS)[number]));
